import { useCompanyVariant } from './useCompanyVariant';
import type { EntityVariant } from '../types/common';

/**
 * Vocabulaire métier swappable selon le variant de l'entité courante.
 *
 * Principe : aucune logique applicative ne change selon le variant — seuls
 * les libellés affichés à l'utilisateur sont remplacés. Une coopérative
 * reste, du point de vue du domaine, une PME multi-succursale ; on remplace
 * juste les mots pour mieux coller à la culture coopérative.
 *
 * Usage :
 * ```tsx
 * const vocab = useEntityVocabulary();
 * return <h2>Vos {vocab.branches}</h2>;
 * // → "Vos succursales" pour une PME
 * // → "Vos coopérants" pour une coopérative
 * ```
 *
 * Pour les libellés qui dépendent du nombre, utilisez les formes au pluriel
 * (`branches`, `members`) ; pour le singulier passez par `branch` / `member`.
 */
export interface EntityVocabulary {
  /** Variant brut, utile pour conditionner sans relire le hook */
  variant: EntityVariant;
  /** L'entité elle-même ("entreprise" / "coopérative") */
  entity: string;
  entityCapitalized: string;
  /** Avec article défini : "l'entreprise" / "la coopérative" */
  entityWithArticle: string;
  /** Avec possessif : "votre entreprise" / "votre coopérative" */
  yourEntity: string;
  /** Avec article + capitale : "L'entreprise" / "La coopérative" */
  entityWithArticleCapitalized: string;
  /** Un membre/employé/coopérant (singulier) */
  member: string;
  /** Les membres (pluriel) */
  members: string;
  /** Une succursale (= un coopérant pour une coop) — singulier */
  branch: string;
  /** Les succursales (pluriel) */
  branches: string;
  branchesCapitalized: string;
  /** Libellé du formulaire "Taille" : "Taille de l'entreprise" vs "Nombre de coopérants" */
  sizeLabel: string;
  /** Libellé court "PME" vs "Coopérative" */
  segmentLabel: string;
  // === Libellés contextuels du formulaire d'identification ===
  /** Titre H1 du formulaire de profil */
  formTitle: string;
  /** Sous-titre du formulaire */
  formSubtitle: string;
  /** Label du logo */
  logoLabel: string;
  /** Message vide quand pas encore enregistré */
  saveBeforeLogo: string;
  /** Titre section pitch */
  pitchTitle: string;
}

const STANDARD: EntityVocabulary = {
  variant: 'standard',
  entity: 'entreprise',
  entityCapitalized: 'Entreprise',
  entityWithArticle: 'l\'entreprise',
  yourEntity: 'votre entreprise',
  entityWithArticleCapitalized: 'L\'entreprise',
  member: 'employé',
  members: 'employés',
  branch: 'succursale',
  branches: 'succursales',
  branchesCapitalized: 'Succursales',
  sizeLabel: 'Taille de l\'entreprise',
  segmentLabel: 'PME',
  formTitle: 'Formulaire d\'Identification d\'Entreprise',
  formSubtitle: 'Informations détaillées sur votre entreprise',
  logoLabel: 'Logo de l\'entreprise',
  saveBeforeLogo: 'Enregistrez d\'abord votre entreprise pour pouvoir ajouter un logo',
  pitchTitle: 'Pitch et Présentation de l\'Entreprise',
};

const COOPERATIVE: EntityVocabulary = {
  variant: 'cooperative',
  entity: 'coopérative',
  entityCapitalized: 'Coopérative',
  entityWithArticle: 'la coopérative',
  yourEntity: 'votre coopérative',
  entityWithArticleCapitalized: 'La coopérative',
  member: 'coopérant',
  members: 'coopérants',
  branch: 'coopérant',
  branches: 'coopérants',
  branchesCapitalized: 'Coopérants',
  sizeLabel: 'Nombre de coopérants',
  segmentLabel: 'Coopérative',
  formTitle: 'Formulaire d\'Identification de Coopérative',
  formSubtitle: 'Informations détaillées sur votre coopérative de détaillants ou d\'artisans',
  logoLabel: 'Logo de la coopérative',
  saveBeforeLogo: 'Enregistrez d\'abord votre coopérative pour pouvoir ajouter un logo',
  pitchTitle: 'Pitch et Présentation de la Coopérative',
};

export function getVocabularyForVariant(variant: EntityVariant): EntityVocabulary {
  return variant === 'cooperative' ? COOPERATIVE : STANDARD;
}

/**
 * Hook React — lit le variant de la company courante et retourne le
 * vocabulaire associé. Stable d'un render à l'autre tant que le variant
 * ne change pas (les objets STANDARD/COOPERATIVE sont des constantes).
 */
export function useEntityVocabulary(): EntityVocabulary {
  const variant = useCompanyVariant();
  return getVocabularyForVariant(variant);
}
